/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {isEmpty} from '@ion/core/src/utils/StringUtil';

/**
 * Search modes available for the Scheduled Reports grid.
 *
 * Each mode maps to a DIFFERENT query parameter of the report-scheduler API
 * (see `buildSearchParams`), not to an extra one:
 *
 *  - `Any`            -> `search`         (schedule name, dashboard title, owner email)
 *  - `RecipientEmail` -> `recipientEmail` (partial match against the schedule's recipient list)
 *
 * They are mutually exclusive by API contract: when `search` is provided the
 * backend ignores every other filter parameter.
 */
export enum SearchType {
  Any = 'any',
  RecipientEmail = 'recipientEmail'
}

/**
 * @getSearchTypeOptions
 *
 * Options rendered by the search-type Dropdown. Lives in this module (instead of
 * inside the grid) so both the grid and `useLoadScheduledReports` can import
 * `SearchType` without creating a circular dependency.
 */
export const getSearchTypeOptions = () => [
  {
    label : translate('sisense:analytics.modularSearch.labels.any'),
    value : SearchType.Any
  },
  {
    label : translate('sisense:analytics.modularSearch.labels.recipientEmail'),
    value : SearchType.RecipientEmail
  }
];

// The `recipientEmail` param takes a list of keywords; users may separate them
// with commas, spaces, or both.
const separatorRegex = /[\s,]+/;

/**
 * @parseSearchKeywords
 *
 * Splits raw user input into a de-duplicated list of keywords. Used for the
 * list-valued API params (currently `recipientEmail`).
 *
 * `'a@x.com,  b@y.com , a@x.com'` -> `['a@x.com', 'b@y.com']`
 */
export const parseSearchKeywords = (value: string): string[] => {
  if (isEmpty(value)) {
    return [];
  }
  const keywords = value
    .split(separatorRegex)
    .map((keyword) => keyword.trim())
    .filter((keyword) => !isEmpty(keyword));

  return Array.from(new Set(keywords));
};

/**
 * @buildSearchParams
 *
 * Translates the committed search term + selected search type into the query
 * params the report-scheduler API expects. Exactly one search param is emitted,
 * because `search` makes the backend ignore all other filters.
 *
 * Returns an empty object when there is nothing to search for, so the caller can
 * spread it without sending empty params.
 */
export const buildSearchParams = (
  searchValue: string,
  searchType: SearchType = SearchType.Any
): GenericObject => {
  if (isEmpty(searchValue)) {
    return {};
  }

  if (searchType === SearchType.RecipientEmail) {
    const recipientEmails = parseSearchKeywords(searchValue);
    return recipientEmails.length
      // The API documents `recipientEmail` as array<string> accepting multiple
      // comma-separated values. Sending the joined string keeps this independent
      // of how the service layer serializes arrays -- swap for `recipientEmails`
      // if `getScheduledReports` already expands arrays into repeated params.
      ? {recipientEmail: recipientEmails.join(',')}
      : {};
  }

  // `filterOn` is the existing alias the Reports service maps onto the API's
  // `search` param. Rename to `search` here if the service forwards params verbatim.
  return {filterOn: searchValue};
};
